import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { createConsultant } from "../Services/consultantService";
import { getConsultantTypes } from "../../../Auth/Services/metadataService";
import { GetEmployeeName } from "../../../Auth/Services/authService";
import Cookies from "js-cookie";
import bgImage from "../../../../Assets/bg3.jpg";
import BulkImportModal from "../Modal/BulkImportModal";
import NormalAlert from "../../../Alerts/NormalAlert";

const AddConsultant = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    consultantCode: "",
    consultantName: "",
    email: "",
    phoneNumber: "",
    consultantType: "",
  });

  const [errors, setErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [alert, setAlert] = useState(null);

  const [consultantTypes, setConsultantTypes] = useState([]);

  useEffect(() => {
    const fetchTypes = async () => {
      const types = await getConsultantTypes();
      setConsultantTypes(types);
    };
    fetchTypes();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.consultantCode.trim())
      newErrors.consultantCode = "Consultant Code is required.";
    if (!formData.consultantName.trim())
      newErrors.consultantName = "Consultant Name is required.";
    if (!formData.phoneNumber.trim())
      newErrors.phoneNumber = "Phone number is required.";
    else if (!/^\d{10}$/.test(formData.phoneNumber))
      newErrors.phoneNumber = "Phone must be exactly 10 digits.";
    if (!formData.email.trim()) newErrors.email = "Email is required.";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Invalid email format.";
    if (!formData.consultantType.trim())
      newErrors.consultantType = "Consultant Type is required.";

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const response = await createConsultant(formData);
      const data = response.data;

      if (data.status === "Success") {
        setAlert({ type: data.status, message: data.message });
        setTimeout(() => {
          navigate("/dashboard");
        }, 1500);
      } else {
        setAlert({
          type: "fail",
          message: data.message || "Something went wrong.",
        });
      }
    } catch (error) {
      setAlert({
        type: "fail",
        message: error?.response?.data?.message || "Server error occurred.",
      });
    }
  };

  const initialFormData = {
    consultantCode: "",
    consultantName: "",
    email: "",
    phoneNumber: "",
    consultantType: "",
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setErrors({});
  };

  useEffect(() => {
    const checkRole = async () => {
      const token = Cookies.get("employeeToken");

      if (!token) {
        navigate("/");
        return;
      }

      try {
        const response = await GetEmployeeName(token);
        const userRole = response?.data?.employeeRole;

        if (userRole !== "Admin Employee") {
          navigate("/Dashboard");
        }
      } catch (err) {
        navigate("/Dashboard");
      }
    };

    checkRole();
  }, []);

  return (
    <>
      <div className="min-h-screen bg-[#f4f6f9]">
        {/* Top Banner */}
        <div
          className="w-full h-[200px] bg-cover bg-center"
          style={{ backgroundImage: `url(${bgImage})` }}
        />

        {/* Form Wrapper */}
        <div className="flex justify-center -mt-20 pb-20 px-4">
          <div className="w-full max-w-[52rem] bg-white rounded-lg shadow-lg p-8 relative">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="flex justify-center mb-3">
                <svg
                  width="70"
                  height="70"
                  viewBox="0 0 24 24"
                  fill="#0467c5"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Head */}
                  <circle cx="12" cy="8" r="4" />

                  {/* Body */}
                  <path d="M4 20c0-3.5 3.5-6 8-6s8 2.5 8 6v1H4v-1z" />
                </svg>
              </div>
              <h2 className="text-2xl font-medium text-[#0467c5]">
                Add Consultant
              </h2>

              <button
                onClick={() => setShowModal(true)}
                className="absolute right-8 top-8 bg-primary text-white px-4 py-2 rounded-md text-sm"
              >
                Bulk Import
              </button>
            </div>

            <hr className="mb-8 border-gray-300" />

            {/* Form */}
            <form
              onSubmit={handleSubmit}
              className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5"
            >
              {/* Consultant Code */}
              <div>
                <label className="block mb-1 text-[14px] font-medium">
                  Consultant Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="consultantCode"
                  value={formData.consultantCode}
                  onChange={handleChange}
                  placeholder="Consultant Code"
                  className={`w-full px-4 py-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 ${
                    errors.consultantCode
                      ? "border-red-500 focus:ring-red-300"
                      : "border-blue-200 focus:ring-blue-300"
                  }`}
                />
                {errors.consultantCode && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.consultantCode}
                  </p>
                )}
              </div>

              {/* Consultant Name */}
              <div>
                <label className="block mb-1 text-[14px] font-medium">
                  Consultant Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="consultantName"
                  value={formData.consultantName}
                  onChange={handleChange}
                  placeholder="Consultant Name"
                  className={`w-full px-4 py-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 ${
                    errors.consultantName
                      ? "border-red-500 focus:ring-red-300"
                      : "border-blue-200 focus:ring-blue-300"
                  }`}
                />
                {errors.consultantName && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.consultantName}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block mb-1 text-[14px] font-medium">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email"
                  className={`w-full px-4 py-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 ${
                    errors.email
                      ? "border-red-500 focus:ring-red-300"
                      : "border-blue-200 focus:ring-blue-300"
                  }`}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block mb-1 text-[14px] font-medium">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phoneNumber"
                  maxLength={10}
                  value={formData.phoneNumber}
                  onChange={(e) => {
                    if (/^\d*$/.test(e.target.value)) handleChange(e);
                  }}
                  placeholder="Phone Number"
                  className={`w-full px-4 py-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 ${
                    errors.phoneNumber
                      ? "border-red-500 focus:ring-red-300"
                      : "border-blue-200 focus:ring-blue-300"
                  }`}
                />
                {errors.phoneNumber && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.phoneNumber}
                  </p>
                )}
              </div>

              {/* Consultant Type */}
              <div className="md:col-span-2">
                <label className="block mb-1 text-[14px] font-medium">
                  Consultant Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="consultantType"
                  value={formData.consultantType}
                  onChange={handleChange}
                  className={`w-full px-4 py-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 ${
                    errors.consultantType
                      ? "border-red-500 focus:ring-red-300"
                      : "border-blue-200 focus:ring-blue-300"
                  }`}
                >
                  <option value="">Select Consultant Type</option>
                  {consultantTypes.map((type) => (
                    <option
                      key={type.consultantTypeID}
                      value={type.consultantTypeID}
                    >
                      {type.consultantTypeName}
                    </option>
                  ))}
                </select>

                {errors.consultantType && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.consultantType}
                  </p>
                )}
              </div>

              {/* Buttons */}
              <div className="md:col-span-2 flex justify-center gap-6 mt-6">
                <button
                  type="submit"
                  className="bg-primary text-white px-8 py-2.5 text-sm rounded-md font-medium transition"
                >
                  Submit
                </button>

                <button
                  type="button"
                  onClick={handleReset}
                  className="bg-gray-200 hover:bg-gray-300 px-8 py-2.5 text-sm rounded-md font-medium transition"
                >
                  Reset
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {showModal && (
        <BulkImportModal
          show={showModal}
          onClose={() => setShowModal(false)}
          setAlert={setAlert}
        />
      )}

      {alert && (
        <NormalAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default AddConsultant;
